import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ErrorService } from '@services/error.service';
import { IntellicheckUploadComponentBase } from '../intellicheck-upload-component-base';
import { IntellicheckUploadStep } from '../intellicheck-upload-step';
import { IDScanResult, RequestedScanType } from '../id-scan-logging';
import { filter, forkJoin, from, takeWhile, timer } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';

import 'dynamsoft-barcode-reader';
import {
    CameraEnhancer,
    CameraView,
    CaptureVisionRouter,
    DCEFrame,
    DecodedBarcodesResult,
    EnumBarcodeFormat,
    EnumImagePixelFormat,
    MultiFrameResultCrossFilter,
} from 'dynamsoft-barcode-reader-bundle';
import { Base64 } from 'js-base64';

//This component uses the BarcodeReader to detect the pdf417 barcode, original implementation of Dynamsoft
//No cropping or sharpening
//Fallback image uses the same reader to find the barcode in a single image
@Component({
    selector: 'app-dynamsoft-scan',
    templateUrl: './dynamsoft-scan.component.html',
    styleUrls: ['../intellicheck-upload.component.scss', './dynamsoft-scan.component.scss'],
})
export class DynamsoftScanComponent extends IntellicheckUploadComponentBase implements OnInit, AfterViewInit, OnDestroy {
    protected moduleElementName: string;
    public moduleName = 'DynamsoftScanComponent';
    protected imageDataFieldName: string;
    private isComponentAlive = true;

    private cvRouter: CaptureVisionRouter = null;
    private cameraView: CameraView = null;
    private cameraEnhancer: CameraEnhancer = null;

    private barcodeData: string;
    private base64Image: string;
    public barcodeFound = false;
    private readonly initialTip = { title: 'Scanning for Barcode', subtitle: 'Align your license with the on screen frame' };
    public currentTip: { title: string; subtitle?: string } = this.initialTip;
    private readonly scanRoiPercentage = 0.7;
    private scanStartTime?: Date;

    private timeoutLength = 15 * 1000; // in ms
    private timeoutTimer: ReturnType<typeof setTimeout>;

    private captureTemplate = 'ReadDenseBarcodes';

    @ViewChild('uploadPreview') previewCanvas: ElementRef<HTMLCanvasElement>;
    private previewCanvasContext: CanvasRenderingContext2D;

    @ViewChild('fallbackInput') fallbackInput: ElementRef<HTMLInputElement>;
    @ViewChild('idOutline') idOutline: ElementRef<HTMLDivElement>;
    @ViewChild('videoContainer') videoContainer: ElementRef<HTMLElement>;

    constructor(
        private readonly errorService: ErrorService,
        private readonly router: Router,
        private readonly _zone: NgZone
    ) {
        super();
    }

    async ngOnInit() {
        window.document.documentElement.style.setProperty('--scan-roi-percentage', this.scanRoiPercentage.toString());
        await this.prepScanner();
    }

    async ngAfterViewInit(): Promise<void> {
        this.isComponentAlive = true;
        this.subscribeToNavigation();
        this.previewCanvasContext = this.previewCanvas.nativeElement.getContext('2d');
        this.doneLoading.emit();
    }

    private async prepScanner() {
        try {
            // CameraView displays the camera feed in a ui component with scan lines and status messages
            this.cameraView = this.cameraView || (await CameraView.createInstance());
            // CameraEnhancer finds cameras to use, activates them, turns on the flash if needed, etc
            this.cameraEnhancer = this.cameraEnhancer || (await CameraEnhancer.createInstance(this.cameraView));
            await this.cameraEnhancer.setResolution({ height: 1080, width: 1920 });

            // CaptureVisionRouter??  ties things together???
            this.cvRouter = this.cvRouter || (await CaptureVisionRouter.createInstance());

            // restrict the barcode formats to what our license allows
            const settings = await this.cvRouter.getSimplifiedSettings(this.captureTemplate);
            settings.barcodeSettings.barcodeFormatIds = EnumBarcodeFormat.BF_PDF417;
            settings.barcodeSettings.localizationModes = [16, 2, 0, 0, 0, 0, 0, 0];
            settings.barcodeSettings.expectedBarcodesCount = 1;
            settings.barcodeSettings.grayscaleTransformationModes[0] = 2;
            await this.cvRouter.updateSettings(this.captureTemplate, settings);

            this.cameraView.setVideoFit('cover');

            // Filter out unchecked and duplicate results.
            const filter = new MultiFrameResultCrossFilter();
            // Filter out unchecked barcodes.
            filter.enableResultCrossVerification('barcode', true);
            // Filter out duplicate barcodes within 3 seconds.
            filter.enableResultDeduplication('barcode', true);
            await this.cvRouter.addResultFilter(filter);

            this.cvRouter.setInput(this.cameraEnhancer);
            this.cvRouter.addResultReceiver({
                onDecodedBarcodesReceived: async (result: DecodedBarcodesResult) => {
                    if (result.barcodeResultItems?.length) {
                        const scan = result.barcodeResultItems[0];
                        this.barcodeData = Base64.encode(scan.text);

                        // get a color image
                        this.cameraEnhancer.setPixelFormat(EnumImagePixelFormat.IPF_ABGR_8888);
                        const image = <DCEFrame>this.cameraEnhancer.fetchImage();
                        this.base64Image = image?.toCanvas()?.toDataURL();

                        if (!this.base64Image) {
                            return;
                        }

                        this.barcodeFound = true;
                        this.currentTip = { title: 'Barcode Found' };
                        clearTimeout(this.timeoutTimer);

                        this.pushScanResults(IDScanResult.Success);
                        this.cameraEnhancer.pause();

                        // show the green 'found' box for 1s(or however long it takes to load the preview image) before continuing for user experience
                        forkJoin([timer(1500), from(this.displayPreview())]).subscribe(() => {
                            // this (onDecodedBarcodesReceived) callback is called from dynamsoft, and angular knows nothing about it.
                            // we need to run within a zone so angular updates the ui afterwards.
                            this._zone.run(() => {
                                this.step = IntellicheckUploadStep.Preview;
                                this.cvRouter.stopCapturing();
                                this.cameraEnhancer.close();
                            });
                        });
                    }
                },
            });

            this.doneLoading.emit();
        } catch (ex) {
            console.error(ex);
            this.doneLoading.emit();
            this.step = IntellicheckUploadStep.Begin;
            this.errorService.showError({
                message: 'Unable to start barcode scanner. Please refresh the page and contact support if the problem continues.',
            });
        }
    }

    async startCapture() {
        try {
            if (this.cameraEnhancer.isPaused()) {
                this.cameraEnhancer.close();
            }

            const uiElem = this.cameraView.getUIElement();
            // attach the CameraView's ui element to the page
            this.videoContainer.nativeElement.replaceChildren(uiElem);

            await this.cameraEnhancer.open();
            await this.cvRouter.startCapturing(this.captureTemplate);

            // Remove the camera selection dropdown from the CameraView's UI element
            uiElem.shadowRoot.querySelector('.dce-sel-camera')?.remove();
            // Remove the resolution selection dropdown from the CameraView's UI element
            uiElem.shadowRoot.querySelector('.dce-sel-resolution')?.remove();
            // remove "powered by dynamsoft"
            uiElem.shadowRoot.querySelector('.dce-msg-poweredby')?.remove();

            this.currentTip = this.initialTip;
            this.step = IntellicheckUploadStep.Capture;
            this.timeoutTimer = setTimeout(() => this.onTimeout(), this.timeoutLength);
            this.scanStartTime = new Date();
        } catch (ex) {
            this.step = IntellicheckUploadStep.Begin;
            this.errorService.showError({
                message: 'Unable to start barcode scanner. Please refresh the page and contact support if the problem continues.',
            });
        }
    }

    continue() {
        this.captureComplete.emit({ barcodeData: this.barcodeData, backData: this.base64Image.split(',')[1] });
    }

    recapture() {
        clearTimeout(this.timeoutTimer);
        this.barcodeFound = false;
        this.currentTip = this.initialTip;
        this.base64Image = null;
        this.barcodeData = null;
        this.startCapture();
    }

    onTimeout = () => {
        this.pushScanResults(IDScanResult.Timeout);
        this.cvRouter.stopCapturing();
        this.cameraEnhancer.close();
        clearTimeout(this.timeoutTimer);
        this.step = IntellicheckUploadStep.Fallback;
    };

    cancelCapture = () => {
        this.pushScanResults(IDScanResult.Back);
        clearTimeout(this.timeoutTimer);
        this.cvRouter.stopCapturing();
        this.cameraEnhancer.close();
        this.step = IntellicheckUploadStep.Begin;
    };

    startFallbackCapture() {
        this.scanStartTime = new Date();
        this.fallbackInput.nativeElement.click();
    }

    async onFallbackCapture(overrideImage: File) {
        this.pushScanResults(IDScanResult.Fallback);
        await this.displayPreview(overrideImage);
        this.step = IntellicheckUploadStep.Preview;
    }

    pushScanResults = (result: IDScanResult) => {
        const currentTime = new Date();
        this.logScanAtttempt.emit({
            type: RequestedScanType.Back,
            typeName: RequestedScanType[RequestedScanType.Back],
            moduleName: this.moduleName,
            result,
            resultName: IDScanResult[result],
            data: null,
            startTime: this.scanStartTime,
            endTime: currentTime,
            elapsedMilliseconds: !this.scanStartTime ? 0 : currentTime.getTime() - this.scanStartTime.getTime(),
            dealerId: null,
            dealId: null,
            docId: null,
        });
    };

    async ngOnDestroy() {
        this.isComponentAlive = false;
        if (this.cameraView && !this.cameraView.disposed) {
            this.cameraView.dispose();
            this.cameraView = null;
        }
        if (this.cameraEnhancer && this.cameraEnhancer.isOpen()) {
            this.cameraEnhancer.close();
            this.cameraEnhancer.dispose();
            this.cameraEnhancer = null;
        }
        if (this.cvRouter && !this.cvRouter.disposed) {
            this.cvRouter.stopCapturing();
            await this.cvRouter.dispose();
            this.cvRouter = null;
        }
    }

    subscribeToNavigation = () => {
        this.router.events
            .pipe(takeWhile(() => this.isComponentAlive))
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                //Since this is the end of the routing path, we can assume that the user is leaving the page if the navigationTrigger is popstate
                if (event.navigationTrigger === 'popstate') {
                    clearTimeout(this.timeoutTimer);
                    this.pushScanResults(IDScanResult.BrowserBack);
                }
            });
    };

    displayPreview(manualPhotoOverride?: Blob): Promise<void> {
        if (manualPhotoOverride) {
            // we only need a loading screen for file uploads
            this.showLoading.emit('Loading Image...');
        }

        return new Promise((resolve) => {
            const canvas = this.previewCanvas.nativeElement;
            const image = new Image();

            image.onload = () => {
                try {
                    canvas.width = image.width;
                    canvas.height = image.height;
                    canvas.style.maxWidth = image.width.toString() + 'px';
                    this.previewCanvasContext.drawImage(image, 0, 0, image.width, image.height);
                } finally {
                    this.doneLoading.emit();
                    resolve();
                }
            };

            if (manualPhotoOverride) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const dataString = event.target.result as string;
                    image.src = dataString;
                    this.base64Image = dataString;
                };
                reader.readAsDataURL(manualPhotoOverride);
            } else {
                image.src = `${this.base64Image}`;
            }
        });
    }
}
